/**
 * Route Permission Registry
 * ═══════════════════════════════════════════════════════════════
 * Single source of truth for "which permission is required to see/access
 * each route". Consumed by:
 *   - Sidebar.jsx       — to hide nav entries the user can't access
 *   - routeConfig.js    — to wire PermissionRoute guards on each route
 *
 * Before this file (C10 fix, 2026-04-20):
 *   Sidebar.jsx had its own PATH_PERMISSIONS map of ~23 entries. routeConfig
 *   had `minRole` on some routes (role-hierarchy only). Two places to keep
 *   in sync. Rename a permission or change a route path, and the sidebar
 *   would silently hide entries the user should see (or vice versa).
 *
 * After this file:
 *   One registry. Sidebar reads from it; routeConfig reads from it.
 *   Renaming a permission or a path is a single-line change.
 *
 * Shape:
 *   {
 *     '/path': 'single:permission',
 *     '/other-path': { anyOf: ['perm1', 'perm2'] },
 *     '/admin-only': { allOf: ['settings:edit_security', 'privacy:manage_breaches'] },
 *   }
 *
 * Lookup rules:
 *   - Exact match wins
 *   - Otherwise longest prefix match (so /projects covers /projects/new,
 *     /projects/:id, etc. unless a more specific override exists)
 *   - Routes not listed here are assumed public to any authenticated user
 */

export const ROUTE_PERMISSIONS = {
  // ── Plan & execute ──
  '/projects':              'projects:view',
  '/projects/new':          'projects:create',
  '/tasks':                 'tasks:view',
  '/sprints':               'sprints:view',
  '/kanban':                'tasks:view',
  '/gantt':                 'tasks:view',
  '/milestones':            'milestones:view',
  '/calendar':              'calendar:view',
  '/portfolio':             'projects:view',

  // ── Collaborate ──
  '/documents':             'documents:view',
  '/knowledge-base':        'knowledgebase:view',
  '/hivelink':              'hivelink:view',

  // ── Track & report ──
  '/timesheets':            'timesheets:view',
  '/budget':                'budget:view',
  '/goals':                 'goals:view',
  '/reports':               'reports:view',

  // ── Accounts & sales ──
  '/accounts':              'accounts:view',
  '/approvals':             'approvals:view',

  // ── People ──
  '/employees':             'members:view',
  '/members':               'members:view',

  // ── Manage ──
  '/capacity':              'capacity:view',
  '/workflows':             'workflows:view',
  '/module-workflows':      'workflows:view',
  '/templates':             'settings:view',
  '/zuno':                  'zuno:use',
  '/client-manage':         'client_portal:manage',
  '/integrations':          'integrations:view',

  // ── Admin / RBAC ──
  '/settings':              'settings:view',
  '/rbac':                  'settings:roles',
  '/admin':                 { anyOf: ['settings:edit_security', 'settings:edit_general', 'privacy:manage_breaches'] },
  '/audit-log':             'audit:view',

  // ── Billing (SC4) ──
  '/billing':               'settings:billing',
};

/**
 * Resolve the permission requirement for a route path.
 * Returns either a string (single permission), an object (`{anyOf}` / `{allOf}`),
 * or null if the route has no gate.
 *
 * Uses longest-prefix match so /projects/123 inherits from /projects.
 * Exact matches take precedence (that's how /projects/new gets projects:create
 * instead of projects:view).
 */
export function resolveRoutePermission(pathname) {
  if (!pathname) return null;

  // Exact match
  if (ROUTE_PERMISSIONS[pathname] !== undefined) return ROUTE_PERMISSIONS[pathname];

  // Longest-prefix match
  let bestMatch = null;
  let bestLen = 0;
  for (const prefix of Object.keys(ROUTE_PERMISSIONS)) {
    if (pathname === prefix || pathname.startsWith(prefix + '/')) {
      if (prefix.length > bestLen) {
        bestLen = prefix.length;
        bestMatch = ROUTE_PERMISSIONS[prefix];
      }
    }
  }
  return bestMatch;
}

/**
 * Flatten a permission spec into props for <PermissionGate> / <PermissionRoute>.
 * Handles both the string form and the {anyOf}/{allOf} object form.
 */
export function toPermissionProps(spec) {
  if (!spec) return {};
  if (typeof spec === 'string') return { perm: spec };
  return { anyOf: spec.anyOf, allOf: spec.allOf };
}
